import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import ShareModal from '../components/ShareModal';
import ImageCropModal from '../components/ImageCropModal';
import { 
  Plus, Search, FileText, Video, Music, Image, File, Lock, 
  ThumbsUp, MessageCircle, Gift, Share2, X, 
  Upload, Check, AlertCircle, Loader2, Edit2, Trash2, 
  CreditCard, Download, ShoppingCart, CheckCircle, Coins, Eye
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Resolve file URLs (relative paths need BACKEND_URL prefix)
const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  if (url.startsWith('http://0.0.0.0') || url.startsWith('http://localhost')) {
    try { return `${BACKEND_URL}${new URL(url).pathname}`; } catch { return url; }
  }
  return url;
};

// Content type icons and colors
const contentTypeConfig = {
  pdf: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50', label: 'PDF', accept: '.pdf' },
  video: { icon: Video, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Vidéo', accept: '.mp4,.mov,.avi,.webm' },
  audio: { icon: Music, color: 'text-green-500', bg: 'bg-green-50', label: 'Podcast', accept: '.mp3,.wav,.ogg' },
  document: { icon: File, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Document', accept: '.doc,.docx,.ppt,.pptx,.xls,.xlsx' },
  image: { icon: Image, color: 'text-pink-500', bg: 'bg-pink-50', label: 'Image', accept: '.jpg,.jpeg,.png,.gif,.webp' },
};

const Shop = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // Brief Jun 11 — filtre segmenté Tous/Gratuit/Payant (reset au rechargement)
  const [priceFilter, setPriceFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ file: 0, thumbnail: 0 });
  const [uploadError, setUploadError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Sprint 1.0c F16 — Edit mode: when set, the Add modal becomes the Edit modal
  // (pre-filled with the product's values, file is read-only).
  const [editingProduct, setEditingProduct] = useState(null);
  // Sprint 1.0c F16 — Comments expand state: { [productId]: { open, list, posting } }
  const [commentsState, setCommentsState] = useState({});
  // Sprint 1.0c F16 — Public runtime config so the CTA logic can switch
  // between "Télécharger direct" (beta gratuit) and "Acheter X CHF" (post-compliance).
  const [forceFreeSessions, setForceFreeSessions] = useState(true);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/config/public`, { withCredentials: false })
      .then(({ data }) => { if (!cancelled) setForceFreeSessions(data.force_free_sessions !== false); })
      .catch(() => { /* fail-open to free flow */ });
    return () => { cancelled = true; };
  }, []);
  
  const fileInputRef = useRef(null);
  const thumbnailInputRef = useRef(null);
  // Brief LOT4.1 — recadrage de la vignette produit avant upload
  const [thumbCropSrc, setThumbCropSrc] = useState(null);

  const [newProduct, setNewProduct] = useState({
    title: '',
    description: '',
    content_type: 'pdf',
    file_url: '',
    file_name: '',
    thumbnail_url: '',
    is_free: true,
    price: 0,
    currency: 'CHF'
  });

  const isOwnShop = currentUser?.user_id === userId;

  // Check payment status from URL params
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const payment = searchParams.get('payment');
    
    if (sessionId && payment === 'success') {
      pollPaymentStatus(sessionId);
    } else if (payment === 'cancelled') {
      setPaymentStatus({ status: 'cancelled', message: 'Paiement annulé' });
      setTimeout(() => setPaymentStatus(null), 5000);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;
    
    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'pending', message: 'Vérification en cours... Consultez votre email pour confirmation.' });
      setCheckingPayment(false);
      return;
    }
    
    setCheckingPayment(true);
    
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
        withCredentials: true
      });
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus({ status: 'success', message: 'Paiement réussi ! Merci pour votre achat.' });
        setCheckingPayment(false);
        loadProducts(); // Reload to show unlocked content
        // Clear URL params
        window.history.replaceState({}, '', `/app/shop/${userId}`);
        return;
      } else if (response.data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Session de paiement expirée.' });
        setCheckingPayment(false);
        return;
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setCheckingPayment(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, [userId]);

  // Brief LOT4.3 (RCA) — le deep-link ?product={id} (partages, notifications,
  // cards produit du Feed) n'était pas géré : la boutique s'ouvrait sans la
  // preview. La ProductCard ciblée s'ouvre désormais automatiquement.
  const deepLinkProductId = searchParams.get('product');
  useEffect(() => {
    if (!deepLinkProductId || loading) return;
    setTimeout(() => {
      document.querySelector(`[data-testid="product-${deepLinkProductId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 300);
  }, [deepLinkProductId, loading]);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/products`, {
        withCredentials: true
      });
      setProducts(response.data.products);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type = 'file') => {
    if (!file) return;
    
    setUploading(true);
    setUploadError('');
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const endpoint = type === 'thumbnail' ? '/upload/thumbnail' : '/upload';
      const response = await axios.post(`${API}${endpoint}`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [type]: progress }));
        }
      });
      
      if (type === 'thumbnail') {
        setNewProduct(prev => ({ ...prev, thumbnail_url: response.data.thumbnail_url }));
      } else {
        setNewProduct(prev => ({ 
          ...prev, 
          file_url: response.data.file_url,
          file_name: file.name,
          content_type: response.data.content_type || prev.content_type
        }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress({ file: 0, thumbnail: 0 });
    }
  };

  const resetNewProduct = () => {
    setNewProduct({
      title: '', description: '', content_type: 'pdf',
      file_url: '', file_name: '', thumbnail_url: '',
      is_free: true, price: 0, currency: 'CHF',
    });
  };

  const handleCreateProduct = async () => {
    if (!newProduct.title) {
      setUploadError('Le titre est requis');
      return;
    }

    try {
      const isEditing = !!editingProduct;
      // Sprint 1.0c F16 — Edit mode uses PUT and excludes the file_url
      // (the brief explicitly forbids replacing the file in this sprint).
      const basePayload = {
        title: newProduct.title,
        description: newProduct.description,
        content_type: newProduct.content_type,
        thumbnail_url: newProduct.thumbnail_url || null,
        is_free: newProduct.is_free,
        price: newProduct.price,
        currency: newProduct.currency,
      };

      if (isEditing) {
        await axios.put(`${API}/products/${editingProduct.product_id}`, basePayload, { withCredentials: true });
      } else {
        await axios.post(`${API}/products`, { ...basePayload, file_url: newProduct.file_url || null }, { withCredentials: true });
      }
      setShowAddModal(false);
      setEditingProduct(null);
      resetNewProduct();
      setUploadError('');
      loadProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de la sauvegarde';
      setUploadError(errorMsg);
    }
  };

  // Sprint 1.0c F16 — open the modal pre-filled with the product's data.
  // File is NOT re-uploadable in this sprint.
  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setNewProduct({
      title: product.title || '',
      description: product.description || '',
      content_type: product.content_type || 'pdf',
      file_url: product.file_url || '',
      file_name: product.file_name || (product.file_url ? product.file_url.split('/').pop() : ''),
      thumbnail_url: product.thumbnail_url || '',
      is_free: !!product.is_free,
      price: product.price || 0,
      currency: product.currency || 'CHF',
    });
    setUploadError('');
    setShowAddModal(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Supprimer ce contenu ?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`, { withCredentials: true });
      loadProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleLikeProduct = async (productId) => {
    // Sprint 1.0c F16 — optimistic UI: toggle locally so the icon animates
    // and recolors instantly. The backend POST is idempotent (returns the
    // canonical state), we resync via loadProducts on completion.
    setProducts((prev) => prev.map((p) => {
      if (p.product_id !== productId) return p;
      const liked = !p.is_liked;
      return {
        ...p,
        is_liked: liked,
        like_count: Math.max(0, (p.like_count || 0) + (liked ? 1 : -1)),
      };
    }));
    try {
      await axios.post(`${API}/products/${productId}/like`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Error liking product:', error);
      // Rollback on error
      loadProducts();
    }
  };

  // Sprint 1.0c F16 — Comments handlers (inline expand pattern like the Feed)
  const handleToggleComments = async (productId) => {
    setCommentsState((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), open: !(prev[productId]?.open) },
    }));
    // Lazy load comments on first open
    const already = commentsState[productId];
    if (!already?.list) {
      try {
        const { data } = await axios.get(`${API}/products/${productId}/comments`, { withCredentials: true });
        setCommentsState((prev) => ({
          ...prev,
          [productId]: { ...(prev[productId] || {}), open: true, list: data.comments || [] },
        }));
      } catch (e) {
        console.error('Error loading product comments:', e);
        setCommentsState((prev) => ({
          ...prev,
          [productId]: { ...(prev[productId] || {}), open: true, list: [] },
        }));
      }
    }
  };

  const handlePostComment = async (productId, content) => {
    const trimmed = (content || '').trim();
    if (!trimmed) return;
    setCommentsState((prev) => ({
      ...prev,
      [productId]: { ...(prev[productId] || {}), posting: true },
    }));
    try {
      const { data } = await axios.post(`${API}/products/${productId}/comments`, { content: trimmed }, { withCredentials: true });
      setCommentsState((prev) => {
        const current = prev[productId] || { list: [] };
        return {
          ...prev,
          [productId]: { ...current, list: [...(current.list || []), data], posting: false },
        };
      });
      setProducts((prev) => prev.map((p) => p.product_id === productId
        ? { ...p, comment_count: (p.comment_count || 0) + 1 }
        : p));
    } catch (e) {
      console.error('Error posting product comment:', e);
      setCommentsState((prev) => ({
        ...prev,
        [productId]: { ...(prev[productId] || {}), posting: false },
      }));
    }
  };

  const handlePurchaseProduct = async (productId) => {
    try {
      // Brief Audit Beta Feb 2026 — new Stripe Connect direct-charge checkout.
      // Money goes directly to the expert's Stripe account (0 % commission
      // beta). Fallback to legacy /checkout/create if the new endpoint isn't
      // available (e.g. Stripe not configured yet).
      let response;
      try {
        response = await axios.post(
          `${API}/shop/products/${productId}/checkout`,
          {},
          { withCredentials: true }
        );
      } catch (primaryErr) {
        // Fallback path — legacy checkout (platform charge)
        if (primaryErr?.response?.status === 404) {
          response = await axios.post(`${API}/checkout/create`, {
            product_id: productId,
            origin_url: window.location.origin
          }, { withCredentials: true });
        } else {
          throw primaryErr;
        }
      }
      const url = response.data.url || response.data.checkout_url;
      if (url) {
        window.location.href = url;
      }
    } catch (error) {
      console.error('Error initiating purchase:', error);
      setPaymentStatus({ 
        status: 'error', 
        message: error.response?.data?.detail || 'Erreur lors de l\'initiation du paiement' 
      });
    }
  };

  // Brief Jun 11 — recherche temps réel (titre + description, null-safe) combinée
  // avec le filtre Gratuit/Payant.
  const filteredProducts = products.filter(p => {
    const q = searchQuery.toLowerCase();
    const matchesQuery = !q
      || (p.title || '').toLowerCase().includes(q)
      || (p.description || '').toLowerCase().includes(q);
    const matchesPrice = priceFilter === 'all'
      || (priceFilter === 'free' ? !!p.is_free : !p.is_free);
    return matchesQuery && matchesPrice;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="shop-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-cyan-500 rounded-2xl p-6 mb-6 text-white">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold mb-2">Boutique</h1>
            <p className="text-white/80">
              {isOwnShop ? 'Partagez vos contenus et monétisez votre expertise' : 'Découvrez les contenus de cet expert'}
            </p>
          </div>
          {isOwnShop && (
            <Button 
              onClick={() => setShowAddModal(true)}
              className="bg-white text-purple-600 hover:bg-white/90 font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Ajouter un contenu
            </Button>
          )}
        </div>
      </div>

      {/* Search Bar + filtre Gratuit/Payant — Brief Jun 11 */}
      <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <Input
              data-testid="shop-search-input"
              placeholder="Rechercher un contenu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-200" data-testid="shop-price-filter">
            {[
              { id: 'all', label: 'Tous' },
              { id: 'free', label: 'Gratuit' },
              { id: 'paid', label: 'Payant' },
            ].map(({ id, label }) => (
              <button
                key={id}
                type="button"
                data-testid={`shop-filter-${id}`}
                onClick={() => setPriceFilter(id)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  priceFilter === id
                    ? 'text-white'
                    : 'bg-transparent text-gray-600 hover:bg-gray-50'
                }`}
                style={priceFilter === id ? { backgroundColor: '#7F77DD' } : undefined}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Payment Status Banner */}
      {paymentStatus && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
          paymentStatus.status === 'success' ? 'bg-green-50 border border-green-200 text-green-700' :
          paymentStatus.status === 'cancelled' ? 'bg-yellow-50 border border-yellow-200 text-yellow-700' :
          paymentStatus.status === 'error' ? 'bg-red-50 border border-red-200 text-red-700' :
          'bg-blue-50 border border-blue-200 text-blue-700'
        }`}>
          {paymentStatus.status === 'success' && <CheckCircle className="h-5 w-5 flex-shrink-0" />}
          {paymentStatus.status === 'error' && <AlertCircle className="h-5 w-5 flex-shrink-0" />}
          {checkingPayment && <Loader2 className="h-5 w-5 animate-spin flex-shrink-0" />}
          <span>{paymentStatus.message}</span>
          <button 
            onClick={() => setPaymentStatus(null)}
            className="ml-auto hover:opacity-70"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {isOwnShop ? 'Aucun contenu pour le moment' : 'Aucun contenu disponible'}
          </h3>
          <p className="text-gray-500 mb-4">
            {isOwnShop ? 'Commencez à partager vos PDF, vidéos, podcasts et plus encore !' : 'Cet utilisateur n\'a pas encore publié de contenu.'}
          </p>
          {isOwnShop && (
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Ajouter votre premier contenu
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.product_id}
              product={product}
              isOwn={isOwnShop}
              userId={userId}
              forceFreeSessions={forceFreeSessions}
              commentsState={commentsState[product.product_id]}
              autoPreview={deepLinkProductId === product.product_id}
              onLike={() => handleLikeProduct(product.product_id)}
              onDelete={() => handleDeleteProduct(product.product_id)}
              onEdit={() => handleEditProduct(product)}
              onPurchase={() => handlePurchaseProduct(product.product_id)}
              onToggleComments={() => handleToggleComments(product.product_id)}
              onPostComment={(content) => handlePostComment(product.product_id, content)}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Product Modal — Sprint 1.0c F16 */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900" data-testid="shop-modal-title">
                  {editingProduct ? 'Modifier le contenu' : 'Ajouter un contenu'}
                </h2>
                <button
                  onClick={() => { setShowAddModal(false); setEditingProduct(null); resetNewProduct(); setUploadError(''); }}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="shop-modal-close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-5">
              {uploadError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{uploadError}</span>
                </div>
              )}

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <Input
                  value={newProduct.title}
                  onChange={(e) => setNewProduct({ ...newProduct, title: e.target.value })}
                  placeholder="Ex: Guide complet du Product Management"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                  placeholder="Décrivez votre contenu..."
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 outline-none resize-none"
                  rows={3}
                />
              </div>

              {/* Content Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type de contenu *</label>
                <div className="grid grid-cols-5 gap-2">
                  {Object.entries(contentTypeConfig).map(([type, config]) => {
                    const Icon = config.icon;
                    return (
                      <button
                        key={type}
                        onClick={() => setNewProduct({ ...newProduct, content_type: type })}
                        className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                          newProduct.content_type === type
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${config.color}`} />
                        <span className="text-xs mt-1 text-gray-600">{config.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fichier</label>
                {editingProduct ? (
                  // Sprint 1.0c F16 — file is read-only in edit mode (brief constraint).
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg" data-testid="shop-modal-file-readonly">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="h-5 w-5 text-gray-500 shrink-0" />
                        <span className="text-sm text-gray-700 truncate">
                          {newProduct.file_name || 'Fichier actuel'}
                        </span>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">Lecture seule</span>
                    </div>
                    <p className="text-xs text-gray-500 italic">
                      Pour remplacer le fichier, supprimez et recréez le produit.
                    </p>
                  </div>
                ) : (
                  <>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={contentTypeConfig[newProduct.content_type]?.accept || '*'}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'file')}
                      className="hidden"
                    />

                    {newProduct.file_url ? (
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Check className="h-5 w-5 text-green-500" />
                          <span className="text-sm text-green-700 truncate max-w-[200px]">
                            {newProduct.file_name || 'Fichier uploadé'}
                          </span>
                        </div>
                        <button
                          onClick={() => setNewProduct({ ...newProduct, file_url: '', file_name: '' })}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all flex flex-col items-center gap-2"
                      >
                        {uploading && uploadProgress.file > 0 ? (
                          <>
                            <Loader2 className="h-8 w-8 text-purple-500 animate-spin" />
                            <span className="text-sm text-gray-600">Upload en cours... {uploadProgress.file}%</span>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-purple-500 h-2 rounded-full transition-all"
                                style={{ width: `${uploadProgress.file}%` }}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <Upload className="h-8 w-8 text-gray-400" />
                            <span className="text-sm text-gray-600">Cliquez pour uploader un fichier</span>
                            <span className="text-xs text-gray-400">
                              {contentTypeConfig[newProduct.content_type]?.accept || 'Tous formats'} • Max 100MB
                            </span>
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
                
                <p className="text-xs text-gray-500 mt-2">
                  Ou collez un lien direct :
                </p>
                <Input
                  value={newProduct.file_url}
                  onChange={(e) => setNewProduct({ ...newProduct, file_url: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                />
              </div>

              {/* Thumbnail Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image de couverture</label>
                <input
                  ref={thumbnailInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    // Brief LOT4.1 — la vignette passe par le recadrage ajustable
                    const reader = new FileReader();
                    reader.onload = () => setThumbCropSrc(reader.result);
                    reader.readAsDataURL(f);
                    e.target.value = '';
                  }}
                  className="hidden"
                />
                
                {newProduct.thumbnail_url ? (
                  <div className="relative">
                    <img 
                      src={resolveUrl(newProduct.thumbnail_url)} 
                      alt="Thumbnail" 
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <button 
                      onClick={() => setNewProduct({ ...newProduct, thumbnail_url: '' })}
                      className="absolute top-2 right-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center text-white hover:bg-black/70"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => thumbnailInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full p-4 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-all flex flex-col items-center gap-2"
                  >
                    {uploading && uploadProgress.thumbnail > 0 ? (
                      <>
                        <Loader2 className="h-6 w-6 text-purple-500 animate-spin" />
                        <span className="text-sm text-gray-600">{uploadProgress.thumbnail}%</span>
                      </>
                    ) : (
                      <>
                        <Image className="h-6 w-6 text-gray-400" />
                        <span className="text-sm text-gray-600">Ajouter une image de couverture</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Pricing */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="block text-sm font-medium text-gray-700 mb-3">Tarification</label>
                <div className="flex gap-3 mb-4">
                  <button
                    onClick={() => setNewProduct({ ...newProduct, is_free: true, price: 0 })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      newProduct.is_free
                        ? 'border-green-500 bg-green-50 text-green-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Gratuit
                  </button>
                  <button
                    onClick={() => setNewProduct({ ...newProduct, is_free: false })}
                    className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                      !newProduct.is_free
                        ? 'border-purple-500 bg-purple-50 text-purple-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Payant
                  </button>
                </div>
                
                {!newProduct.is_free && (
                  <div className="flex gap-3">
                    <Input
                      data-testid="shop-price-input"
                      type="number"
                      min="0"
                      step="0.5"
                      value={newProduct.price === 0 ? '' : newProduct.price}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setNewProduct({
                          ...newProduct,
                          price: raw === '' ? 0 : parseFloat(raw) || 0,
                        });
                      }}
                      placeholder="Prix"
                      className="flex-1"
                    />
                    <select
                      value={newProduct.currency}
                      onChange={(e) => setNewProduct({ ...newProduct, currency: e.target.value })}
                      className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-300"
                    >
                      <option value="CHF">CHF</option>
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3">
              <Button
                variant="outline"
                onClick={() => { setShowAddModal(false); setEditingProduct(null); resetNewProduct(); setUploadError(''); }}
                className="flex-1"
                data-testid="shop-modal-cancel"
              >
                Annuler
              </Button>
              <Button
                onClick={handleCreateProduct}
                disabled={!newProduct.title || uploading}
                className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500"
                data-testid="shop-modal-submit"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingProduct ? 'Enregistrer les modifications' : 'Publier'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Brief LOT4.1 — recadrage ajustable de la vignette produit */}
      <ImageCropModal
        isOpen={!!thumbCropSrc}
        imageSrc={thumbCropSrc}
        onClose={() => setThumbCropSrc(null)}
        aspect={16 / 9}
        aspectOptions={[
          { label: '16:9', value: 16 / 9 },
          { label: '4:3', value: 4 / 3 },
          { label: 'Carré', value: 1 },
        ]}
        title="Recadrer la vignette"
        onCropComplete={(file) => { setThumbCropSrc(null); handleFileUpload(file, 'thumbnail'); }}
      />
    </div>
  );
};

// Product Card Component — Sprint 1.0c F16 refonte
const ProductCard = ({
  product, isOwn, userId, forceFreeSessions,
  commentsState, autoPreview, onLike, onDelete, onEdit, onPurchase, onToggleComments, onPostComment,
}) => {
  const config = contentTypeConfig[product.content_type] || contentTypeConfig.document;
  const Icon = config.icon;
  const [purchasing, setPurchasing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  // Phase B5 P2 §2 — Product preview modal (click card → open detailed view)
  // Brief LOT4.3 — s'ouvre automatiquement sur deep-link ?product={id}.
  const [showPreview, setShowPreview] = useState(!!autoPreview);
  // Sprint 1.0c F16 — like animation tick (replays the bounce on every click)
  const [likeBouncing, setLikeBouncing] = useState(false);
  // Sprint 1.0c F16 — inline comment composer
  const [draftComment, setDraftComment] = useState('');

  const handleLikeClick = () => {
    setLikeBouncing(true);
    setTimeout(() => setLikeBouncing(false), 200);
    onLike();
  };

  const handlePurchaseClick = async () => {
    setPurchasing(true);
    await onPurchase();
    setPurchasing(false);
  };

  const handleDownload = () => {
    if (product.file_url) {
      window.open(resolveUrl(product.file_url), '_blank');
    }
  };

  // Sprint 1.0c F16 — CTA conditional logic. The brief codified 3 states:
  //   • product is free                        → "Télécharger" (direct)
  //   • product paid && already purchased     → "Télécharger" (direct)
  //   • product paid && not purchased:
  //       - FORCE_FREE_SESSIONS=true (beta)    → "Télécharger" (free flow)
  //       - FORCE_FREE_SESSIONS=false (prod)   → "Acheter X CHF"  (Stripe)
  // `product.is_locked` is set server-side based on FORCE_FREE + purchase state.
  // We keep that contract but also fall back to client-side FORCE_FREE so the
  // CTA flips immediately when ENV changes without requiring a backend roll.
  const isPaidNotPurchased = !product.is_free && !product.is_purchased;
  const showBuyCta = isPaidNotPurchased && !forceFreeSessions && product.is_locked !== false;

  const commentsList = commentsState?.list || [];
  const commentsOpen = !!commentsState?.open;
  const postingComment = !!commentsState?.posting;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-all flex flex-col" data-testid={`product-${product.product_id}`}>
        {/* Thumbnail — Sprint 1.0c F16: fixed height for grid consistency.
            Phase B5 P2 §2 — click on thumbnail opens detailed preview modal. */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowPreview(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPreview(true); } }}
          className="relative h-40 bg-gradient-to-br from-gray-800 to-gray-900 shrink-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-400"
          data-testid={`product-preview-open-${product.product_id}`}
        >
          {product.thumbnail_url ? (
            <img
              src={resolveUrl(product.thumbnail_url)}
              alt={product.title}
              className="w-full h-full object-cover object-center"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Icon className={`h-16 w-16 ${config.color} opacity-50`} />
            </div>
          )}

          {/* Overlay for locked content */}
          {product.is_locked && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <div className="text-center text-white">
                <Lock className="h-10 w-10 mx-auto mb-2" />
                <p className="text-2xl font-bold">{product.price} {product.currency}</p>
              </div>
            </div>
          )}

          {/* Price badge (visitor view) — Sprint 1.0c F16: hide if owner overlay
              takes the same spot to avoid stacking */}
          {!product.is_free && !product.is_locked && !isOwn && (
            <div className="absolute top-3 right-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
              {product.price} {product.currency}
            </div>
          )}
          {product.is_purchased && !product.is_free && !isOwn && (
            <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1 z-10">
              <Check className="h-3 w-3" />
              Acheté
            </div>
          )}
          {product.is_free && !isOwn && (
            <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
              Gratuit
            </div>
          )}

          {/* Share button */}
          <button
            onClick={(e) => { e.stopPropagation(); setShowShareModal(true); }}
            className="absolute top-3 left-3 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-white hover:bg-white/30 transition-all z-10"
            data-testid={`share-${product.product_id}`}
          >
            <Share2 className="h-4 w-4" />
          </button>

          {/* Content type badge — Sprint 1.0c F16:
              z-10 + white/95 plate so it's always readable, regardless of the cover image. */}
          <div
            className="absolute bottom-3 left-3 z-10 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 bg-white/95 dark:bg-gray-900/90 shadow-md backdrop-blur-sm"
            data-testid={`type-badge-${product.product_id}`}
          >
            <Icon className={`h-3 w-3 ${config.color}`} />
            <span className={config.color}>{config.label}</span>
          </div>

          {/* Owner actions — Sprint 1.0c F16: pencil now WIRED to onEdit */}
          {isOwn && (
            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-blue-500 shadow-lg"
                title="Modifier"
                data-testid={`edit-${product.product_id}`}
              >
                <Edit2 className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-gray-600 hover:text-red-500 shadow-lg"
                title="Supprimer"
                data-testid={`delete-${product.product_id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>

        {/* Content — Sprint 1.0c F16: fixed minimum block height for grid alignment.
            Phase B5 P2 §2 — title also opens the preview modal. */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => setShowPreview(true)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowPreview(true); } }}
          className="p-4 flex-1 flex flex-col min-h-[110px] cursor-pointer focus:outline-none focus:bg-purple-50/50"
          data-testid={`product-title-${product.product_id}`}
        >
          <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2 leading-snug" title={product.title}>
            {product.title}
          </h3>
          {product.description ? (
            <p className="text-sm text-gray-500 line-clamp-2">{product.description}</p>
          ) : (
            <p className="text-sm text-gray-400 italic">Aucune description</p>
          )}
        </div>

        {/* Actions — Brief LOT6.3 : alignement vertical homogène (items-center)
            + hauteur uniforme des pills pour éviter le décalage du "Give tip". */}
        <div className="px-4 pb-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <div className="flex items-center gap-3 min-h-[32px]">
            {/* Sprint 1.0c F16: like with scale animation + fill on active state */}
            <button
              onClick={handleLikeClick}
              className={`flex items-center gap-1 transition-all duration-150 ${
                product.is_liked ? 'text-[#7F77DD]' : 'text-gray-500 hover:text-[#7F77DD]'
              } ${likeBouncing ? 'scale-110' : 'scale-100'}`}
              data-testid={`like-${product.product_id}`}
            >
              <ThumbsUp className={`h-4 w-4 ${product.is_liked ? 'fill-current' : ''}`} />
              <span className="text-sm">{product.like_count || 0}</span>
            </button>
            <button
              onClick={onToggleComments}
              className={`flex items-center gap-1 transition-colors ${
                commentsOpen ? 'text-blue-500' : 'text-gray-500 hover:text-blue-500'
              }`}
              data-testid={`comments-${product.product_id}`}
              title="Commentaires"
            >
              <MessageCircle className="h-4 w-4" />
              <span className="text-sm">{product.comment_count || 0}</span>
            </button>
            {/* Brief B5 P2 §8 — "Give tip" sur tous les produits (gratuits + payants),
                outline ambre, désactivé en beta tant que FORCE_FREE_SESSIONS=true
                (Stripe pas encore réactivé pour compliance). */}
            {!isOwn && (
              <button
                type="button"
                disabled={forceFreeSessions}
                title={forceFreeSessions ? 'Disponible après la beta' : 'Envoyer un pourboire'}
                onClick={() => { /* TODO V1.5 : ouvrir modal Stripe tip */ }}
                className={`inline-flex items-center gap-1 h-8 px-2.5 rounded-full border text-xs font-semibold transition-colors self-center ${
                  forceFreeSessions
                    ? 'border-amber-200 text-amber-400 bg-amber-50/50 cursor-not-allowed'
                    : 'border-amber-500 text-amber-700 hover:bg-amber-50'
                }`}
                data-testid={`give-tip-${product.product_id}`}
              >
                <Coins className="h-3.5 w-3.5" /> Give tip
              </button>
            )}
          </div>

          {/* CTA — Sprint 1.0c F16 conditional logic (see ProductCard hook above) */}
          {showBuyCta ? (
            <Button
              size="sm"
              onClick={handlePurchaseClick}
              disabled={purchasing}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
              data-testid={`buy-${product.product_id}`}
            >
              {purchasing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <CreditCard className="h-4 w-4 mr-1" />
              )}
              Acheter — {product.price} {product.currency}
            </Button>
          ) : product.file_url ? (
            <Button
              size="sm"
              onClick={handleDownload}
              variant="outline"
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
              data-testid={`download-${product.product_id}`}
            >
              <Download className="h-4 w-4 mr-1" />
              Télécharger
            </Button>
          ) : null}
        </div>

        {/* Sprint 1.0c F16 — Inline comments section */}
        {commentsOpen && (
          <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-3 space-y-2" data-testid={`comments-section-${product.product_id}`}>
            {commentsList.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">Aucun commentaire pour le moment — lance la discussion !</p>
            ) : (
              <ul className="space-y-2 max-h-48 overflow-y-auto">
                {commentsList.map((c) => (
                  <li key={c.comment_id} className="flex items-start gap-2" data-testid={`comment-${c.comment_id}`}>
                    {c.author_avatar ? (
                      <img src={resolveUrl(c.author_avatar)} alt={c.author_name} className="h-6 w-6 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="h-6 w-6 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-200 text-[10px] font-bold flex items-center justify-center shrink-0">
                        {(c.author_name || '?').substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs"><span className="font-semibold text-gray-900 dark:text-white">{c.author_name || 'Anonyme'}</span> <span className="text-gray-700 dark:text-gray-200">{c.content}</span></p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <form
              onSubmit={(e) => { e.preventDefault(); if (!draftComment.trim() || postingComment) return; onPostComment(draftComment); setDraftComment(''); }}
              className="flex items-center gap-2"
            >
              <Input
                value={draftComment}
                onChange={(e) => setDraftComment(e.target.value)}
                placeholder="Écrire un commentaire…"
                className="flex-1 text-sm"
                disabled={postingComment}
                data-testid={`comment-input-${product.product_id}`}
              />
              <Button
                type="submit"
                size="sm"
                disabled={!draftComment.trim() || postingComment}
                className="bg-purple-500 hover:bg-purple-600 text-white"
                data-testid={`comment-submit-${product.product_id}`}
              >
                {postingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Publier'}
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        shareType="product"
        shareData={{
          id: product.product_id,
          userId: userId,
          title: product.title,
          description: product.description || 'Découvrez ce contenu sur OpenSapiens'
        }}
      />

      {/* Phase B5 P2 §2 — Product preview modal */}
      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="product-preview-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPreview(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Cover */}
            <div className="relative h-56 bg-gradient-to-br from-gray-800 to-gray-900">
              {product.thumbnail_url ? (
                <img
                  src={resolveUrl(product.thumbnail_url)}
                  alt={product.title}
                  className="w-full h-full object-cover object-center"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon className={`h-20 w-20 ${config.color} opacity-50`} />
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowPreview(false)}
                className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60"
                data-testid="product-preview-close"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="absolute bottom-3 left-3 px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 bg-white/95 shadow-md backdrop-blur-sm">
                <Icon className={`h-3.5 w-3.5 ${config.color}`} />
                <span className={config.color}>{config.label}</span>
              </div>
              {!product.is_free && (
                <div className="absolute top-3 left-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  {product.price} {product.currency}
                </div>
              )}
              {product.is_free && (
                <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                  Gratuit
                </div>
              )}
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-900" data-testid="product-preview-title">
                  {product.title}
                </h2>
                <div className="flex items-center gap-4 text-sm text-gray-500 shrink-0">
                  <span className="flex items-center gap-1">
                    <ThumbsUp className={`h-4 w-4 ${product.is_liked ? 'fill-current text-[#7F77DD]' : ''}`} />
                    {product.like_count || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="h-4 w-4" />
                    {product.comment_count || 0}
                  </span>
                </div>
              </div>
              {product.description ? (
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="product-preview-description">
                  {product.description}
                </p>
              ) : (
                <p className="text-sm text-gray-400 italic">Aucune description fournie.</p>
              )}

              {/* CTA */}
              <div className="flex flex-wrap gap-3 pt-2">
                {showBuyCta ? (
                  <Button
                    onClick={handlePurchaseClick}
                    disabled={purchasing}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                    data-testid="product-preview-cta"
                  >
                    {purchasing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CreditCard className="h-4 w-4 mr-2" />}
                    Acheter — {product.price} {product.currency}
                  </Button>
                ) : product.file_url ? (
                  <Button
                    onClick={handleDownload}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                    data-testid="product-preview-cta"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                ) : null}
                <Button
                  variant="outline"
                  onClick={() => { setShowPreview(false); setShowShareModal(true); }}
                  className="text-purple-600 border-purple-200 hover:bg-purple-50"
                  data-testid="product-preview-share"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Partager
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { if (!commentsOpen) onToggleComments(); }}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  data-testid="product-preview-comments-toggle"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  {commentsOpen ? 'Commentaires affichés' : 'Voir les commentaires'}
                </Button>
              </div>

              {/* Inline comments inside preview when expanded */}
              {commentsOpen && (
                <div className="pt-4 border-t border-gray-100 space-y-3" data-testid="product-preview-comments">
                  {commentsList.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">Aucun commentaire pour le moment.</p>
                  ) : (
                    <ul className="space-y-2 max-h-48 overflow-y-auto">
                      {commentsList.map((c) => (
                        <li key={c.comment_id} className="flex items-start gap-2">
                          {c.author_avatar ? (
                            <img src={resolveUrl(c.author_avatar)} alt={c.author_name} className="h-6 w-6 rounded-full object-cover shrink-0" />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold flex items-center justify-center shrink-0">
                              {(c.author_name || '?').substring(0, 2).toUpperCase()}
                            </div>
                          )}
                          <p className="text-xs flex-1"><span className="font-semibold text-gray-900">{c.author_name || 'Anonyme'}</span> <span className="text-gray-700">{c.content}</span></p>
                        </li>
                      ))}
                    </ul>
                  )}
                  <form
                    onSubmit={(e) => { e.preventDefault(); if (!draftComment.trim() || postingComment) return; onPostComment(draftComment); setDraftComment(''); }}
                    className="flex items-center gap-2"
                  >
                    <Input
                      value={draftComment}
                      onChange={(e) => setDraftComment(e.target.value)}
                      placeholder="Écrire un commentaire…"
                      className="flex-1 text-sm"
                      disabled={postingComment}
                      data-testid={`product-preview-comment-input-${product.product_id}`}
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!draftComment.trim() || postingComment}
                      className="bg-purple-500 hover:bg-purple-600 text-white"
                      data-testid={`product-preview-comment-submit-${product.product_id}`}
                    >
                      {postingComment ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Publier'}
                    </Button>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Shop;
